import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, Bot, Search, Zap, ClipboardCheck, FileText, PenTool,
  Sparkles, ArrowRight, Shield, Clock, BarChart3, Globe, CheckCircle, Star,
  MessageCircle, TrendingUp
} from "lucide-react";

const PRODUCTS = [
  {
    id: "aireceptionist",
    title: "AI Receptionist",
    tagline: "Your 24/7 Digital Front Desk",
    description: "Intelligent virtual receptionist powered by AI. Handles customer inquiries, routes messages to the right department, and provides instant multilingual responses.",
    icon: Bot,
    color: "#0ea5e9",
    gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    url: "https://aireceptionist.myeuropacific.com",
    badge: "AI-Powered",
    features: ["Multilingual support", "Smart routing", "24/7 availability", "CRM integration"],
    status: "Live",
  },
  {
    id: "seooptimisation",
    title: "SEO Optimisation",
    tagline: "Rank Higher, Get Found Faster",
    description: "Analyse and optimise web content for search engines. Get actionable recommendations, keyword analysis, and competitive insights to improve your rankings.",
    icon: Search,
    color: "#8b5cf6",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    url: "https://seooptimisation.myeuropacific.com",
    badge: "Analytics",
    features: ["Keyword analysis", "Content scoring", "Competitor tracking", "Technical audit"],
    status: "Live",
  },
  {
    id: "seoautomationhub",
    title: "SEO Automation Hub",
    tagline: "Automate Your SEO at Scale",
    description: "Schedule audits, track keyword rankings across markets, and manage content optimisation pipelines effortlessly with automated workflows.",
    icon: Zap,
    color: "#f59e0b",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    url: "https://SEOAutomationHub.myeuropacific.com/",
    badge: "Automation",
    features: ["Scheduled audits", "Rank tracking", "Pipeline management", "Bulk optimisation"],
    status: "Live",
  },
  {
    id: "surveyorsuite",
    title: "Surveyor Suite",
    tagline: "Cargo Inspection, Simplified",
    description: "End-to-end cargo inspection and survey management. Schedule surveys, assign surveyors, generate branded reports, and track inspection status across all shipments.",
    icon: ClipboardCheck,
    color: "#10b981",
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    url: "https://surveyorsuite.myeuropacific.com",
    badge: "Logistics",
    features: ["Survey scheduling", "Report generation", "Photo evidence", "Surveyor assignment"],
    status: "Live",
  },
  {
    id: "invoiceintake",
    title: "Invoice Intake",
    tagline: "From PDF to Ledger, Automatically",
    description: "AI-powered inbound invoice parsing and processing. Automatically extract data from supplier invoices, validate against POs, and push to your accounting program.",
    icon: FileText,
    color: "#ef4444",
    gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    url: "https://invoiceintake.myeuropacific.com",
    badge: "AI + Finance",
    features: ["OCR extraction", "Auto-matching", "Accounting sync", "Approval workflow"],
    status: "Live",
  },
  {
    id: "o365signature",
    title: "O365 Signature Manager",
    tagline: "One Brand, Every Email",
    description: "Centrally manage and deploy professional email signatures across your entire Microsoft 365 organisation. Consistent branding, marketing banners, and compliance for all staff.",
    icon: PenTool,
    color: "#0078d4",
    gradient: "linear-gradient(135deg, #0078d4 0%, #005a9e 100%)",
    url: "https://signatureforge.myeuropacific.com",
    badge: "Microsoft 365",
    features: ["Central management", "Marketing banners", "Compliance ready", "Auto-deploy"],
    status: "Live",
  },
  {
    id: "attendio",
    title: "Attendio",
    tagline: "Clock In, Track Time, Stay Compliant",
    description: "Staff attendance and leave management kiosk. Employees clock in/out via tablet or phone, request leave, and managers approve in real time. Full compliance reporting included.",
    icon: Clock,
    color: "#06b6d4",
    gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    url: "https://attendio.myeuropacific.com",
    badge: "HR & Kiosk",
    features: ["Kiosk check-in", "Leave requests", "Manager approvals", "Compliance reports"],
    status: "Live",
  },
  {
    id: "cargocfs",
    title: "CargoCFS",
    tagline: "Warehouse Operations, Under Control",
    description: "Full warehouse management system for CFS operations. Truck scheduling, ramp management, cargo tracking by BL, photo documentation, damage reports, and real-time customer portal.",
    icon: Globe,
    color: "#1d4ed8",
    gradient: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
    url: "https://mycargo-cfs.com",
    badge: "WMS & CFS",
    features: ["Truck scheduling", "Ramp management", "BL cargo tracking", "Damage reports"],
    status: "Live",
  },
  {
    id: "lasovita",
    title: "Lasovita",
    tagline: "Marina Management, All in One",
    description: "Complete boating operations platform. Manage boat reservations, track invoices, handle maintenance tickets, and coordinate marina activities from a single dashboard.",
    icon: Star,
    color: "#0d9488",
    gradient: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
    url: "https://lasovita.myeuropacific.com",
    badge: "Marina & Boating",
    features: ["Boat reservations", "Invoice tracking", "Maintenance tickets", "Marina dashboard"],
    status: "Live",
  },
  {
    id: "whatsappscanner",
    title: "WhatsApp Scanner",
    tagline: "Turn WhatsApp Chats into Structured Data",
    description: "Scan, parse, and index WhatsApp conversations and shared documents. Pulls out PF numbers, container IDs, invoices and quote requests, then routes them into the right CRM workflow automatically.",
    icon: MessageCircle,
    color: "#25D366",
    gradient: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
    url: "https://whatsappscanner.myeuropacific.com",
    badge: "AI + WhatsApp",
    features: ["Chat parsing", "Document OCR", "PF / container auto-detect", "CRM routing"],
    status: "Live",
  },
  {
    id: "logigrowthai",
    title: "LogiGrowth.AI",
    tagline: "The AI Revenue OS for B2B Logistics",
    description: "Autonomous AI agents that find logistics prospects, research accounts, personalise outreach and book meetings — safely, at scale. Eight specialised agents run prospecting, research, content, engagement, CRM and analytics 24/7.",
    icon: TrendingUp,
    color: "#3b82f6",
    gradient: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #06b6d4 100%)",
    url: "https://logigrowth-ai.emergent.host",
    badge: "AI Revenue OS",
    features: ["8 autonomous agents", "Logistics signal scoring", "LinkedIn-safe outreach", "HubSpot / Salesforce sync"],
    status: "Live",
  },
  {
    id: "roketcustomsbot",
    title: "Roket Customs Bot",
    tagline: "EU Customs Classification & Declaration, Automated",
    description: "Autonomous EU customs agent (FURS). Classifies goods to the correct TARIC/HS code, looks up duties and VAT, screens for licences and compliance flags, and turns inbound emails into broker-ready declaration data.",
    icon: Shield,
    color: "#4f46e5",
    gradient: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
    url: "https://customsbot.myeuropacific.com",
    badge: "AI + Customs",
    features: ["TARIC / HS classification", "Duty & VAT lookup", "Compliance screening", "Broker-ready output"],
    status: "Live",
  },
  {
    id: "epasone",
    title: "EPAS.ONE",
    tagline: "Logistics Network Site & Partner Portal",
    description: "Public network website plus members portal for the EPAS logistics network. Partner dashboard, shipment tracking, news feed, holiday calendars, document downloads and self-service vendor onboarding with subscription tiers.",
    icon: Globe,
    color: "#0891b2",
    gradient: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
    url: "https://epasone.myeuropacific.com",
    badge: "Network Portal",
    features: ["Partner dashboard", "Shipment tracking", "News & holidays", "Vendor onboarding"],
    status: "Live",
  },
];

function ProductCard({ product, onLaunch, featured }) {
  const [hovered, setHovered] = useState(false);
  const Icon = product.icon;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`utility-${product.id}`}
      style={{
        background: hovered ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.025)",
        border: `1px solid ${hovered ? product.color + "60" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 16,
        padding: featured ? "32px 28px" : "24px 22px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered ? `0 12px 40px ${product.color}15` : "none",
        cursor: "pointer",
      }}
      onClick={() => onLaunch(product)}
    >
      {/* Glow effect */}
      <div style={{
        position: "absolute", top: -40, right: -40, width: 120, height: 120,
        background: `radial-gradient(circle, ${product.color}12 0%, transparent 70%)`,
        borderRadius: "50%",
      }} />

      {/* Badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <span style={{
          background: `${product.color}18`, color: product.color,
          padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
          letterSpacing: "0.3px", display: "flex", alignItems: "center", gap: 4,
        }}>
          <Sparkles size={11} /> {product.badge}
        </span>
        <span style={{
          background: product.status === "Live" ? "#22c55e18" : "#f59e0b18",
          color: product.status === "Live" ? "#22c55e" : "#f59e0b",
          padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600,
        }}>
          {product.status}
        </span>
      </div>

      {/* Icon */}
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: product.gradient,
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 16, boxShadow: `0 4px 16px ${product.color}30`,
      }}>
        <Icon size={26} color="white" strokeWidth={1.8} />
      </div>

      {/* Content */}
      <h3 style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 700, margin: "0 0 4px", letterSpacing: "-0.3px" }}>
        {product.title}
      </h3>
      <p style={{ color: product.color, fontSize: 13, fontWeight: 500, margin: "0 0 10px" }}>
        {product.tagline}
      </p>
      <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6, margin: "0 0 18px" }}>
        {product.description}
      </p>

      {/* Features */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
        {product.features.map((f, i) => (
          <span key={i} style={{
            display: "flex", alignItems: "center", gap: 4,
            color: "#64748b", fontSize: 11, fontWeight: 500,
            background: "rgba(255,255,255,0.04)", padding: "3px 10px", borderRadius: 8,
          }}>
            <CheckCircle size={10} color="#22c55e" /> {f}
          </span>
        ))}
      </div>

      {/* CTA */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: product.gradient, color: "white",
          padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          boxShadow: `0 2px 12px ${product.color}30`,
          transition: "transform 0.2s",
          transform: hovered ? "scale(1.02)" : "scale(1)",
        }}>
          Launch App <ArrowRight size={14} />
        </span>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          color: "#64748b", fontSize: 12,
          padding: "8px 14px", borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.08)",
        }}>
          <ExternalLink size={12} /> Details
        </span>
      </div>
    </div>
  );
}

export default function UtilitiesPage() {
  const navigate = useNavigate();

  const handleLaunch = (product) => {
    if (product.url && product.url !== "#") {
      window.open(product.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#060b18", fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}>
      {/* Nav */}
      <header style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "14px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }} data-testid="back-to-home">
          <ArrowLeft size={16} /> Back to myEuropacific
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ color: "#475569", fontSize: 12 }}>{PRODUCTS.length} solutions</span>
        </div>
      </header>

      {/* Hero */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 24px 20px", textAlign: "center", position: "relative" }}>
        {/* Background glow */}
        <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 600, height: 300, background: "radial-gradient(ellipse, rgba(56,189,248,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />
        
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.15)", borderRadius: 20, padding: "5px 16px", marginBottom: 20, fontSize: 12, color: "#38bdf8", fontWeight: 500 }}>
          <Sparkles size={13} /> Built by Europacific
        </div>
        
        <h1 style={{ color: "#f1f5f9", fontSize: 42, fontWeight: 800, margin: "0 0 12px", lineHeight: 1.15, letterSpacing: "-1px" }}>
          Logistics Solutions,<br />
          <span style={{ background: "linear-gradient(135deg, #38bdf8, #8b5cf6, #f59e0b)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Ready to Deploy
          </span>
        </h1>
        <p style={{ color: "#64748b", fontSize: 16, maxWidth: 560, margin: "0 auto 32px", lineHeight: 1.6 }}>
          AI-powered tools and automation platforms built for freight forwarding, customs, and supply chain operations. Each solution works standalone or integrated with your systems.
        </p>

        {/* Trust badges */}
        <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 48 }}>
          {[
            { icon: Shield, label: "Enterprise Security" },
            { icon: Clock, label: "24/7 Uptime" },
            { icon: Globe, label: "Multi-language" },
            { icon: BarChart3, label: "Analytics Built-in" },
          ].map((b, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, color: "#475569", fontSize: 12 }}>
              <b.icon size={14} color="#38bdf8" /> {b.label}
            </div>
          ))}
        </div>
      </section>

      {/* Products Grid */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 60px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 20 }}>
          {PRODUCTS.map((product) => (
            <ProductCard key={product.id} product={product} onLaunch={handleLaunch} />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "40px 24px", textAlign: "center" }}>
        <p style={{ color: "#475569", fontSize: 13, margin: "0 0 8px" }}>
          Need a custom solution for your logistics operation?
        </p>
        <a href="mailto:rok.kobal@europacific.com" style={{ color: "#38bdf8", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
          Contact us <ArrowRight size={14} style={{ verticalAlign: "middle" }} />
        </a>
        <p style={{ color: "#1e293b", fontSize: 11, marginTop: 24 }}>Europacific d.o.o.</p>
      </section>
    </div>
  );
}
